import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListBuyingGroupsInput } from "@nexxt/contracts/party";
import { partyQueries } from "@nexxt/party-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the buying groups of HubSpot (50-api/party.md §2.10, ADR-110, ADR-116): by name, and one with its
// contacts. Read only: HubSpot is their master.

const groups = api.party["buying-groups"];
const byId = groups[":buyingGroupId"];

type ListPage = InferResponseType<typeof groups.$get, 200>;
export type BuyingGroupSummary = ListPage["items"][number];

/** The filters of the list input (`listBuyingGroupsInput`), without the paging the query adds. */
export type BuyingGroupFilters = Pick<ListBuyingGroupsInput, "q"> & {
  archived?: boolean | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: [...partyQueries.all, "buying-groups"] as const,
  lists: [...partyQueries.all, "buying-groups", "list"] as const,
};

export const buyingGroupQueries = {
  ...keys,

  list: (filters: BuyingGroupFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          groups.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  detail: (buyingGroupId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", buyingGroupId],
      queryFn: ({ signal }) => unwrap(byId.$get({ param: { buyingGroupId } }, { init: { signal } })),
    }),
};
